// Primitivas compartidas de dibujo en canvas para los informes descargables
// con marca Jidokap (extraídas de features/directivo/informeEjecutivo.ts para
// que el Informe Gerencial del coordinador las reutilice — 2026-09).
//
// SIEMPRE paleta clara: son documentos para compartir/imprimir, no vistas de
// pantalla. Cero dependencias (canvas nativo + lib/pdf.ts).
//
// Nota: features/supervisor/resumenImagen.ts tiene su propia paleta dual
// (oscura/clara de pantalla) y layout PNG — migrarlo acá sería invasivo sin
// beneficio; queda anotado como deuda menor.

export const SERIF = '"Instrument Serif", Georgia, serif';
export const SANS = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
export const MONO = '"JetBrains Mono", ui-monospace, Consolas, Menlo, monospace';

/** Paleta clara (documento) — mismos valores del light del supervisor. */
export const P = {
  pageBg: '#f6f3ee',
  surface: '#ffffff',
  border: '#e4e0d6',
  text: '#181621',
  muted: '#6d6a61',
  faint: '#9b968b',
  headBg: '#1b1b1f',
  headFg: '#f5f5f2',
  zebra: '#faf8f4',
  // Sincronizado con tokens.css (DN6) — no editar a mano sin tocar ambos.
  accent: '#7a1e2e',
  ok: '#1a7f4e',
  warn: '#8c660b',
  crit: '#c8371f',
  info: '#2b5d8a',
  barTrack: '#e7e4dc',
  /** Serie de datos (DN6, capa dataviz) = --chart-1 light. Nunca marca ni semáforo. */
  serie: '#2a78d6',
} as const;

export async function cargarFuentes(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load(`34px ${SERIF}`),
      document.fonts.load(`600 13px ${SANS}`),
      document.fonts.load(`15px ${MONO}`),
      document.fonts.load(`500 15px ${MONO}`),
    ]);
  } catch {
    // Sin red o API no soportada → fallbacks del stack.
  }
}

// ── Formatters es-CO ────────────────────────────────────────────────────────

export const miles = (n: number): string =>
  Math.round(n).toLocaleString('es-CO', { maximumFractionDigits: 0 });
export const pctFmt = (n: number | null): string => (n === null ? 'n/s' : `${Math.round(n)} %`);
export const horasFmt = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
export const compacto = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  if (abs >= 10_000)
    return `${(n / 1_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} k`;
  return miles(n);
};
export const copM = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `$${(n / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  return `$${miles(n)}`;
};

// ── Primitivas de dibujo ────────────────────────────────────────────────────

export interface AreaDibujo {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Wordmark "Jidokap." (punto en acento). Devuelve el ancho dibujado. */
export function drawWordmark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  fg: string,
): number {
  ctx.textAlign = 'left';
  ctx.font = `${size}px ${SERIF}`;
  ctx.fillStyle = fg;
  ctx.fillText('Jidokap', x, y);
  const w = ctx.measureText('Jidokap').width;
  ctx.fillStyle = P.accent;
  ctx.fillText('.', x + w, y);
  return w + ctx.measureText('.').width;
}

/** Card con título de sección; devuelve el área interior para dibujar. */
export function drawCardSeccion(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  titulo: string,
): AreaDibujo {
  ctx.fillStyle = P.surface;
  roundRect(ctx, x, y, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.font = `600 12px ${SANS}`;
  ctx.fillStyle = P.text;
  ctx.fillText(titulo, x + 18, y + 24);
  return { x: x + 18, y: y + 44, w: w - 36, h: h - 62 };
}

/**
 * Barras verticales dobles (serie A en gris vs serie B en acento). Leyenda
 * configurable — el informe ejecutivo usa Programada/Real; el gerencial la usa
 * para comparativos de período.
 */
export function drawBarrasDuales(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  datos: { label: string; programada: number | null; real: number }[],
  leyenda: readonly [string, string] = ['Programada', 'Real'],
): void {
  const max = Math.max(1, ...datos.map((d) => Math.max(d.programada ?? 0, d.real)));
  const n = datos.length;
  const grupoW = area.w / Math.max(1, n);
  const barW = Math.min(22, grupoW / 2.8);
  const plotH = area.h - 18;
  datos.forEach((d, i) => {
    const cx = area.x + grupoW * i + grupoW / 2;
    ctx.textAlign = 'center';
    if (d.programada !== null && d.programada > 0) {
      const h = (d.programada / max) * plotH;
      ctx.fillStyle = P.barTrack;
      roundRect(ctx, cx - barW - 2, area.y + plotH - h, barW, h, 2);
      ctx.fill();
      ctx.font = `9px ${MONO}`;
      ctx.fillStyle = P.faint;
      ctx.fillText(compacto(d.programada), cx - barW / 2 - 2, area.y + plotH - h - 7);
    }
    if (d.real > 0) {
      const h = (d.real / max) * plotH;
      ctx.fillStyle = P.accent;
      roundRect(ctx, cx + 2, area.y + plotH - h, barW, h, 2);
      ctx.fill();
      ctx.font = `600 9px ${MONO}`;
      ctx.fillStyle = P.text;
      ctx.fillText(compacto(d.real), cx + barW / 2 + 2, area.y + plotH - h - 7);
    }
    ctx.font = `10px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.fillText(d.label, cx, area.y + plotH + 11);
  });
  // Leyenda
  ctx.textAlign = 'left';
  ctx.font = `10px ${SANS}`;
  let lx = area.x;
  const ly = area.y - 14;
  for (const [color, label] of [
    [P.barTrack, leyenda[0]],
    [P.accent, leyenda[1]],
  ] as const) {
    ctx.fillStyle = color;
    ctx.fillRect(lx, ly - 4, 8, 8);
    ctx.fillStyle = P.muted;
    ctx.fillText(label, lx + 12, ly);
    lx += 12 + ctx.measureText(label).width + 14;
  }
}

/** Línea simple (perfil horario / serie) con etiqueta del pico. */
export function drawLinea(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  datos: { label: string; valor: number }[],
  color: string,
  labelCada = 4,
): void {
  const max = Math.max(1, ...datos.map((d) => d.valor));
  const plotH = area.h - 18;
  const stepX = area.w / Math.max(1, datos.length - 1);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  datos.forEach((d, i) => {
    const x = area.x + stepX * i;
    const y = area.y + plotH - (d.valor / max) * plotH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.font = `9px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.textAlign = 'center';
  datos.forEach((d, i) => {
    if (i % labelCada === 0) ctx.fillText(d.label, area.x + stepX * i, area.y + plotH + 11);
  });
  // Etiqueta del PICO: la hora más productiva es el dato accionable del perfil.
  if (datos.length > 0) {
    const iMax = datos.reduce((im, d, i) => (d.valor > datos[im]!.valor ? i : im), 0);
    const px = area.x + stepX * iMax;
    const py = area.y + plotH - (datos[iMax]!.valor / max) * plotH;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(px, py, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = `600 9px ${MONO}`;
    ctx.fillStyle = P.text;
    const cerca = px > area.x + area.w - 40;
    ctx.textAlign = cerca ? 'right' : 'center';
    ctx.fillText(
      `${datos[iMax]!.label} h · ${compacto(datos[iMax]!.valor)}`,
      cerca ? px : Math.max(px, area.x + 30),
      py - 9,
    );
    ctx.textAlign = 'center';
  }
}

/** Barras horizontales (causas de paro). */
export function drawBarrasH(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  datos: { nombre: string; minutos: number }[],
  vacioMsg = 'Sin paros que afecten disponibilidad esta semana',
): void {
  if (datos.length === 0) {
    ctx.font = `12px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.textAlign = 'left';
    ctx.fillText(vacioMsg, area.x, area.y + 20);
    return;
  }
  const max = Math.max(1, ...datos.map((d) => d.minutos));
  const filaH = Math.min(34, area.h / datos.length);
  const labelW = 150;
  datos.forEach((d, i) => {
    const y = area.y + filaH * i + filaH / 2;
    ctx.font = `11px ${SANS}`;
    ctx.fillStyle = P.muted;
    ctx.textAlign = 'left';
    const nombre = d.nombre.length > 22 ? `${d.nombre.slice(0, 21)}…` : d.nombre;
    ctx.fillText(nombre, area.x, y);
    const barX = area.x + labelW;
    const barMaxW = area.w - labelW - 74;
    const bw = Math.max(2, (d.minutos / max) * barMaxW);
    // Misma medida, misma tinta: el ranking ya lo da el orden. El rojo se
    // reserva para alarmas con regla (política de color 3 capas).
    ctx.fillStyle = P.serie;
    roundRect(ctx, barX, y - 7, bw, 14, 3);
    ctx.fill();
    ctx.font = `11px ${MONO}`;
    ctx.fillStyle = P.text;
    ctx.fillText(horasFmt(d.minutos), barX + bw + 8, y);
  });
}

/** Dona de distribución del tiempo (productivo vs paro). */
export function drawDona(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  tiempo: { productivasH: number; paroH: number },
): void {
  const total = tiempo.productivasH + tiempo.paroH;
  const cx = area.x + area.h / 2 - 6;
  const cy = area.y + area.h / 2 - 8;
  const r = Math.min(area.h / 2 - 14, 62);
  if (total <= 0) {
    ctx.font = `12px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.textAlign = 'left';
    ctx.fillText('Sin horas capturadas esta semana', area.x, area.y + 20);
    return;
  }
  const fracParo = tiempo.paroH / total;
  const start = -Math.PI / 2;
  ctx.lineWidth = 22;
  ctx.strokeStyle = P.ok;
  ctx.beginPath();
  ctx.arc(cx, cy, r, start + fracParo * Math.PI * 2, start + Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = P.crit;
  ctx.beginPath();
  ctx.arc(cx, cy, r, start, start + fracParo * Math.PI * 2);
  ctx.stroke();
  ctx.font = `600 15px ${MONO}`;
  ctx.fillStyle = P.text;
  ctx.textAlign = 'center';
  ctx.fillText(`${Math.round((1 - fracParo) * 100)} %`, cx, cy - 6);
  ctx.font = `9px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.fillText('productivo', cx, cy + 10);
  // Leyenda a la derecha
  let ly = cy - 18;
  ctx.textAlign = 'left';
  for (const [color, label, val] of [
    [P.ok, 'Horas productivas', tiempo.productivasH],
    [P.crit, 'Horas de paro', tiempo.paroH],
  ] as const) {
    ctx.fillStyle = color;
    ctx.fillRect(cx + r + 26, ly - 4, 8, 8);
    ctx.font = `11px ${SANS}`;
    ctx.fillStyle = P.muted;
    ctx.fillText(label, cx + r + 40, ly);
    ctx.font = `500 11px ${MONO}`;
    ctx.fillStyle = P.text;
    ctx.fillText(
      `${val.toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`,
      cx + r + 40,
      ly + 15,
    );
    ly += 38;
  }
}

export const TENDENCIA_COLORS = [P.accent, P.info, P.warn, P.ok, P.crit] as const;

export interface TendenciaInforme {
  plantas: string[];
  semanas: { label: string; valores: (number | null)[] }[];
}

/** Tendencia multi-serie (cumplimiento % por semana) con guía del 100%. */
export function drawTendencia(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  t: TendenciaInforme,
): void {
  const plotH = area.h - 18;
  const n = t.semanas.length;
  if (n === 0) {
    ctx.font = `12px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.textAlign = 'left';
    ctx.fillText('Sin historia de semanas anteriores', area.x, area.y + 20);
    return;
  }
  const stepX = area.w / Math.max(1, n - 1);
  const yDe = (pct: number) => area.y + plotH - (Math.min(pct, 120) / 120) * plotH;
  // Guía 100%
  ctx.strokeStyle = P.border;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(area.x, yDe(100));
  ctx.lineTo(area.x + area.w, yDe(100));
  ctx.stroke();
  ctx.setLineDash([]);
  t.plantas.forEach((_, pi) => {
    const color = TENDENCIA_COLORS[pi % TENDENCIA_COLORS.length]!;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    let started = false;
    t.semanas.forEach((s, i) => {
      const v = s.valores[pi];
      if (v === null || v === undefined) return;
      const x = area.x + stepX * i;
      if (!started) {
        ctx.moveTo(x, yDe(v));
        started = true;
      } else ctx.lineTo(x, yDe(v));
    });
    ctx.stroke();
    // Etiquetas de valor en cada punto: arriba la primera serie, abajo la
    // segunda (evita choques cuando las líneas se cruzan).
    ctx.font = `600 9px ${MONO}`;
    t.semanas.forEach((s, i) => {
      const v = s.valores[pi];
      if (v === null || v === undefined) return;
      const x = area.x + stepX * i;
      const y = yDe(v);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.text;
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(v)}%`, x, pi % 2 === 0 ? y - 9 : y + 12);
    });
  });
  ctx.font = `9px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.textAlign = 'center';
  t.semanas.forEach((s, i) => ctx.fillText(s.label, area.x + stepX * i, area.y + plotH + 11));
  // Leyenda
  ctx.textAlign = 'left';
  let lx = area.x;
  const ly = area.y - 14;
  t.plantas.forEach((nombre, pi) => {
    ctx.fillStyle = TENDENCIA_COLORS[pi % TENDENCIA_COLORS.length]!;
    ctx.fillRect(lx, ly - 4, 8, 8);
    ctx.font = `10px ${SANS}`;
    ctx.fillStyle = P.muted;
    ctx.fillText(nombre, lx + 12, ly);
    lx += 12 + ctx.measureText(nombre).width + 14;
  });
}
